import { Button, Flex, Space, Spin, Table, type TableProps } from 'antd'
import { useState } from 'react'

import { FeatureFlagService } from '@services/FeatureFlagService'
import { useConfirmation } from '@store/confirmation'
import { useNotifications } from '@store/notifications'

import type { FeatureFlag } from '../../../types'

import { AddFeatureFlagModal } from './components/AddFeatureFlagModal'

type FeatureFlagsTabProps = {
    featureFlags: FeatureFlag[] | undefined
    refetchFlags: () => void
    refetchRules: () => void
}
export const FeatureFlagsTab = ({ featureFlags, refetchFlags, refetchRules }: FeatureFlagsTabProps) => {
    const { showNotification } = useNotifications()
    const [isModalOpen, setIsModalOpen] = useState(false)
    const confirm = useConfirmation()

    const onAddFeatureFlag = async (key: string) => {
        await FeatureFlagService.create({ key })
        await refetchFlags()
    }

    const columns: TableProps<FeatureFlag>['columns'] = [
        {
            title: 'Id',
            dataIndex: 'id',
            key: 'id'
        },
        {
            title: 'Key',
            dataIndex: 'key',
            key: 'key'
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Space size="middle">
                    <Button
                        type="link"
                        onClick={async () => {
                            if (
                                await confirm({
                                    title: 'Are you sure you want to delete this feature flag?',
                                    description: 'This will remove all users from the accessing this feature',
                                    confirmText: 'Delete'
                                })
                            ) {
                                try {
                                    await FeatureFlagService.delete(record.id)

                                    showNotification({
                                        title: 'Feature flag has been deleted'
                                    })

                                    await refetchFlags()
                                    await refetchRules()
                                } catch (err) {
                                    showNotification({
                                        title: 'Feature flag could not be deleted',
                                        type: 'error'
                                    })
                                }
                            }
                        }}
                    >
                        Delete
                    </Button>
                </Space>
            )
        }
    ]

    if (!featureFlags) return <Spin />

    return (
        <>
            <Button onClick={() => setIsModalOpen(true)}>Add Feature Flag</Button>
            <Flex gap="middle" vertical>
                <Table columns={columns} dataSource={featureFlags} rowKey="id" pagination={false} />
            </Flex>
            <AddFeatureFlagModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onAddFeatureFlag={onAddFeatureFlag}
            />
        </>
    )
}
